// CabinetPage — page du cabinet virtuel (/cabinet/:slug) — MAQUETTE = source de vérité (v5.22.0).
// Barre AYA (← Retour · logo · partage/favori/menu) · cover + « Voir les photos » · Cabinet ouvert ·
// chips domaines · bienvenue · 3 actions · « Dernière demande » · onglets · Notre cabinet ·
// highlights · carousel Expérience · page Notre équipe (avis, infos pratiques, domaines détaillés).
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Bookmark, CalendarCheck, Camera, FolderOpen, MessageCircle, MoreHorizontal, Share2, Video, Zap } from "lucide-react";
import { T, StepHeader, PresenceDot } from "@/components/cabinet/cabinetTheme";
import { CabinetOrient } from "@/components/cabinet/CabinetOrient";
import { CabinetMembers } from "@/components/cabinet/CabinetMembers";
import { CabinetSlots } from "@/components/cabinet/CabinetSlots";
import { OpenCard, Highlights, ExperienceCarousel, TeamSection, AvailableBanner, ReviewsCarousel, InfosPratiques, DomainesDetail } from "@/components/cabinet/CabinetSections";
import { PhotoGallery, MemberProfileSheet } from "@/components/cabinet/CabinetOverlays";

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = [["presentation", "Présentation"], ["equipe", "Équipe"], ["avis", "Avis"], ["infos", "Infos"]];

export default function CabinetPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [step, setStep] = useState("home");
  const [tab, setTab] = useState("presentation");
  const [qualif, setQualif] = useState(null);
  const [member, setMember] = useState(null);
  const [booked, setBooked] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [profile, setProfile] = useState(null);
  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 760;

  // Attribution : conserver la source (AYA_SEARCH) tout au long du parcours.
  const srcKey = `aya_cab_src_${slug}`;
  if (sp.get("src") === "aya_search") sessionStorage.setItem(srcKey, "AYA_SEARCH");
  const source = sessionStorage.getItem(srcKey) === "AYA_SEARCH" ? "AYA_SEARCH" : "DIRECT";

  useEffect(() => {
    axios.get(`${API}/api/v3/cabinet/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
    axios.post(`${API}/api/v3/cabinet/${slug}/track`, {
      type: "cabinet_visit",
      source: sp.get("src") === "aya_search" ? "AYA_SEARCH" : "DIRECT",
      q: sp.get("q") || null,
    }).catch(() => {});
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return (
    <div style={{ ...T.page, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }} data-testid="cabinet-not-found">
      <div style={T.sub}>Cabinet introuvable.</div>
      <button style={T.btnOutline} onClick={() => navigate("/search")}>← Retour à la recherche</button>
    </div>
  );
  if (!data) return <div style={{ ...T.page, display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280" }}>Chargement…</div>;

  const cab = data.cabinet;
  const photos = cab.photos?.length ? cab.photos : [cab.cover_photo].filter(Boolean);
  const avail = data.availability || { open: false, available_now: 0, avatars: [], others: 0 };
  const bookMember = (m) => { setProfile(null); setMember(m); setQualif(null); setStep("slots"); };
  const overlays = (
    <>
      {gallery != null && <PhotoGallery photos={photos} startIndex={gallery} onClose={() => setGallery(null)} />}
      {profile && <MemberProfileSheet slug={slug} member={profile} hours={cab.hours} onBook={bookMember} onClose={() => setProfile(null)} />}
    </>
  );
  const goBack = () => {
    if (step === "home") navigate(-1);
    else if (step === "slots") setStep("members");
    else setStep("home");
  };
  // Partage WhatsApp : aperçu du cabinet + lien direct (acquisition de clients).
  const share = () => {
    const txt = `\u2696\uFE0F *${cab.name}* — ${cab.tagline}\n${cab.verified ? "\u2705 Cabinet vérifié AYA · " : ""}\u{1F4CD} ${cab.commune}, ${cab.city}\n\n${(cab.domains || []).slice(0, 4).join(" · ")}\n\n\u{1F4AC} Orientation par AYA, rendez-vous en ligne ou en visio :\n${window.location.origin}/cabinet/${slug}`;
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(txt)}`, "_blank", "noopener");
  };

  // Écrans 3/4/5 : en-tête maquette (← · AYA · badge) + contenu de l'étape.
  if (step !== "home") return (
    <div style={T.page} data-testid={`cabinet-page-${slug}`}>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "0 16px 16px" }}>
        <StepHeader onBack={goBack} testid="cabinet-back"
          right={step === "orient" ? <span style={{ ...T.badge, background: "#065F46", color: "#fff" }}>BETA</span> : null} />
        {step === "orient" && (
          <CabinetOrient slug={slug} source={source}
            onPickInterlocutor={(q) => { setQualif(q); setStep("members"); }}
            onBook={(q) => { setQualif(q); setStep("members"); }} />
        )}
        {step === "members" && (
          <CabinetMembers members={data.members} qualification={qualif} onProfile={setProfile}
            onSelect={(m) => { setMember(m); setStep("slots"); }} />
        )}
        {step === "slots" && member && (
          <CabinetSlots slug={slug} member={member} qualification={qualif} source={source}
            onBooked={(apt) => { setBooked(apt); setStep("confirmed"); }} />
        )}
        {step === "confirmed" && booked && (
          <div style={{ ...T.card, textAlign: "center", padding: 22 }} data-testid="cabinet-confirmed">
            <div style={{ fontSize: 36 }}>✅</div>
            <h2 style={{ ...T.h1, fontSize: 18, margin: "8px 0 4px" }}>Votre rendez-vous est confirmé</h2>
            <div style={{ ...T.sub, lineHeight: 1.7 }}>
              Avec : <b style={{ color: "#111827" }}>{booked.member?.name}</b><br />
              Date : <b style={{ color: "#111827" }}>{booked.date}</b> · Heure : <b style={{ color: "#111827" }}>{booked.time}</b><br />
              Durée : {booked.duration_min} min · {booked.mode === "visio" ? "📹 Visioconférence" : "Au cabinet"}<br />
              Type : {booked.type}
            </div>
            <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
              {booked.mode === "visio" && (
                <button style={T.btnGreen} onClick={() => navigate(`/cabinet/${slug}/visio/${booked.appointment_id}`)} data-testid="confirmed-join-visio">
                  <Video size={15} /> Rejoindre la visio
                </button>
              )}
              <button style={T.btnOutline} onClick={() => navigate(`/cabinet/${slug}/dossier`)} data-testid="confirmed-open-dossier">
                <FolderOpen size={14} /> Voir mon dossier
              </button>
            </div>
          </div>
        )}
      </div>
      {overlays}
    </div>
  );

  // PAGE DU CABINET VIRTUEL (maquette écran 1).
  return (
    <div style={T.page} data-testid={`cabinet-page-${slug}`}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        {/* Barre AYA : ← Retour · logo · partage / favori / menu */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px" }} data-testid="cabinet-topbar">
          <button onClick={goBack} data-testid="cabinet-back"
            style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", color: "#111827", fontSize: 13.5, fontWeight: 700, cursor: "pointer", padding: 0 }}>
            ‹ Retour
          </button>
          <span style={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>AYA<span style={{ color: "#F97316" }}>●</span></span>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <button onClick={share} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#111827" }} aria-label="Partager" data-testid="cabinet-share-btn"><Share2 size={17} /></button>
            <button onClick={() => toast.success("Cabinet ajouté à vos favoris")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#111827" }} aria-label="Enregistrer" data-testid="cabinet-save-btn"><Bookmark size={17} /></button>
            <button onClick={() => toast.info("Menu bientôt disponible")} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#111827" }} aria-label="Menu" data-testid="cabinet-menu-btn"><MoreHorizontal size={18} /></button>
          </div>
        </div>

        {/* COVER + « Voir les photos » + logo rond */}
        <div style={{ position: "relative" }}>
          {cab.cover_photo ? (
            <img src={cab.cover_photo} alt={cab.name} style={{ width: "100%", height: 158, objectFit: "cover", display: "block", background: "#F3F4F6" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="cabinet-cover" />
          ) : <div style={{ height: 158, background: "linear-gradient(135deg, #04281A, #065F46)" }} />}
          <button onClick={() => setGallery(0)} style={{ position: "absolute", right: 12, bottom: 12, display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 11px", borderRadius: 999, border: "none", background: "rgba(17,24,39,0.72)", color: "#fff", fontSize: 10.5, fontWeight: 700, cursor: "pointer" }} data-testid="cabinet-photos-badge">
            <Camera size={11} /> Voir les photos <span style={{ opacity: 0.75 }}>1/{photos.length}</span>
          </button>
          <div style={{ position: "absolute", left: 16, bottom: -26, width: 60, height: 60, borderRadius: "50%", background: "#0B1324", border: "3px solid #fff", boxShadow: "0 1px 6px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>⚖️</div>
        </div>

        <div style={{ padding: "34px 16px 0", display: "grid", gap: 11, minWidth: 0 }}>
          {/* Identité + carte Cabinet ouvert */}
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 190 }}>
              <h1 style={T.h1} data-testid="cabinet-name">{String(cab.name).toUpperCase()}</h1>
              <div style={{ ...T.sub, fontWeight: 600, marginTop: 1 }}>{cab.tagline}</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6, flexWrap: "wrap", fontSize: 12.5 }}>
                {cab.verified && <span style={{ color: "#0E8A4F", fontWeight: 800 }} data-testid="cabinet-verified-badge">✅ Cabinet vérifié AYA</span>}
                {cab.is_demo && <span style={{ ...T.badge, ...T.badgeDemo }} data-testid="cabinet-demo-badge">DEMO</span>}
              </div>
              <div style={{ ...T.sub, marginTop: 4 }}>📍 {cab.commune}, {cab.city}</div>
            </div>
            {avail.open && <OpenCard availability={avail} onClick={() => setTab("equipe")} />}
          </div>

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="cabinet-domains">
            {(cab.domains || []).map((d) => <span key={d} style={T.chip}>{d}</span>)}
          </div>

          <div style={{ ...T.card, background: "#ECFDF5", border: "1px solid rgba(14,138,79,0.2)", padding: "12px 14px" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#065F46" }}>👋 Bienvenue dans notre cabinet virtuel</div>
            <p style={{ ...T.sub, margin: "3px 0 0", lineHeight: 1.45 }}>Expliquez-nous votre situation et nous vous orienterons vers le bon interlocuteur.</p>
          </div>

          {/* 3 ACTIONS EN LIGNE — hiérarchie exacte maquette */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
            <button onClick={() => setStep("orient")} data-testid="cabinet-action-orient"
              style={{ padding: "13px 6px", borderRadius: 12, border: "1.5px solid #0E8A4F", background: "#fff", color: "#0E8A4F", cursor: "pointer", display: "grid", gap: 5, justifyItems: "center" }}>
              <MessageCircle size={19} /><span style={{ fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}>Discuter<br />avec AYA</span>
            </button>
            <button onClick={() => { setQualif(null); setStep("members"); }} data-testid="cabinet-action-interlocutor"
              style={{ position: "relative", padding: "13px 6px", borderRadius: 12, border: "none", background: "#0E8A4F", color: "#fff", cursor: "pointer", display: "grid", gap: 5, justifyItems: "center" }}>
              {avail.available_now > 0 && <PresenceDot size={8} style={{ position: "absolute", top: 8, right: 9 }} />}
              <Video size={19} /><span style={{ fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}>Parler<br />en visio</span>
            </button>
            <button onClick={() => { setQualif(null); setStep("members"); }} data-testid="cabinet-action-book"
              style={{ padding: "13px 6px", borderRadius: 12, border: "none", background: "#F97316", color: "#fff", cursor: "pointer", display: "grid", gap: 5, justifyItems: "center" }}>
              <CalendarCheck size={19} /><span style={{ fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}>Prendre<br />rendez-vous</span>
            </button>
          </div>

          {/* ⚡ Dernière demande reçue */}
          {data.last_request_min != null && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #E5E7EB", borderRadius: 12, padding: "9px 13px", background: "#FAFAF9" }} data-testid="cabinet-last-request">
              <Zap size={14} color="#F59E0B" fill="#F59E0B" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", flex: 1 }}>Dernière demande reçue il y a {data.last_request_min} min</span>
              <span style={{ color: "#9CA3AF", fontSize: 13 }}>›</span>
            </div>
          )}

          {/* ONGLETS */}
          <div style={{ display: "flex", gap: 18, borderBottom: "1px solid #E5E7EB" }} data-testid="cabinet-tabs">
            {TABS.map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} style={{ ...T.tab, ...(tab === v ? T.tabOn : {}) }} data-testid={`cabinet-tab-${v}`}>{l}</button>
            ))}
          </div>

          {tab === "presentation" && (
            <div style={{ display: "grid", gap: 14, minWidth: 0 }} data-testid="cabinet-presentation">
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h2 style={{ ...T.h2, fontSize: 15 }}>Notre cabinet</h2>
                  <button onClick={() => navigate(`/cabinet/${slug}/dossier`)} data-testid="cabinet-open-dossier"
                    style={{ background: "none", border: "none", color: "#0E8A4F", fontSize: 12, fontWeight: 800, cursor: "pointer", padding: 0 }}>
                    Mon dossier →
                  </button>
                </div>
                <p style={{ ...T.sub, fontSize: 13, lineHeight: 1.55, margin: "6px 0 0" }}>{cab.presentation}</p>
              </div>
              {cab.highlights?.length > 0 && <Highlights highlights={cab.highlights} />}
              <ExperienceCarousel cabinetName={cab.name} references={data.references} />
            </div>
          )}
          {tab === "equipe" && (
            <div style={{ display: "grid", gap: 18, minWidth: 0 }} data-testid="cabinet-team">
              <TeamSection members={data.members} onProfile={setProfile} />
              {avail.open && <AvailableBanner availability={avail} />}
              <ReviewsCarousel reviews={data.reviews} />
              <InfosPratiques cab={cab} isDesktop={isDesktop} onOpenGallery={() => setGallery(0)} />
              <DomainesDetail domains={cab.domains || []} isDesktop={isDesktop} />
            </div>
          )}
          {tab === "avis" && (
            <div data-testid="cabinet-reviews">
              {data.reviews?.length
                ? <ReviewsCarousel reviews={data.reviews} />
                : <div style={T.sub}>Les avis clients arrivent bientôt.</div>}
            </div>
          )}
          {tab === "infos" && (
            <div style={{ display: "grid", gap: 18 }} data-testid="cabinet-infos">
              <InfosPratiques cab={cab} isDesktop={isDesktop} onOpenGallery={() => setGallery(0)} />
              <DomainesDetail domains={cab.domains || []} isDesktop={isDesktop} />
            </div>
          )}
        </div>
      </div>
      {overlays}
    </div>
  );
}
